// @avantia common-code
import { determineEnvironment, ensureDefined, voidFunction } from '@avantia/client-and-server-utilities';
import { HpError } from '@avantia/logging-and-errors';
import jQuery from 'jquery';
import { HpCustomEventTypes } from '../types';
import {
  HpBindFieldChangeEventsProps,
  HpCustomEventHandler,
  HpRegisterBespokeCustomEventProps,
  HpTriggerBespokeCustomEventFunction,
  HpTriggerCommonEventProps,
  HpTriggerEventProps,
  HpTriggerFieldEventProps,
  HpTriggerGeneralActionProps,
  HpTriggerHelpInfoEventProps,
  HpTriggerLinkFollowedEventProps,
  HpTriggerModalActionProps,
  HpTriggerNavigationBarEventProps,
  HpTriggerNavigationEventProps,
  HpTriggerPageLoadEventProps,
  HpTriggerPageLoadEventWithCommonProps,
  HpTriggerToggleElementProps
} from './eventActionTypes';

const { isClient } = determineEnvironment();
const jwin: JQuery<Window & typeof globalThis> | null = isClient ? jQuery(window) : null;

export let triggerFieldChangedEvent: HpTriggerCustomEventFunction<HpTriggerFieldEventProps> = triggerNotYetInitialized;

export let triggerElementToggledEvent: HpTriggerCustomEventFunction<HpTriggerToggleElementProps> =
  triggerNotYetInitialized;

export let triggerModalActionEvent: HpTriggerCustomEventFunction<HpTriggerModalActionProps> = triggerNotYetInitialized;

export let triggerNavigationChangedEvent: HpTriggerCustomEventFunction<HpTriggerNavigationEventProps> =
  triggerNotYetInitialized;

export let triggerNavigationBarEvent: HpTriggerCustomEventFunction<HpTriggerNavigationBarEventProps> =
  triggerNotYetInitialized;

let triggerPageLoadEventPrivate: HpTriggerCustomEventFunction<HpTriggerPageLoadEventWithCommonProps> =
  triggerNotYetInitialized;

export function triggerPageLoadEvent(props: HpTriggerPageLoadEventProps): void {
  // We don't need the common props, so they aren't provided, but we need to
  // make the below call think the props are provided so we don't break the typing system.
  triggerPageLoadEventPrivate(props as HpTriggerPageLoadEventWithCommonProps);
}

export let triggerGeneralActionEvent: HpTriggerCustomEventFunction<HpTriggerGeneralActionProps> =
  triggerNotYetInitialized;

export let triggerHelpInfoEvent: HpTriggerCustomEventFunction<HpTriggerHelpInfoEventProps> = triggerNotYetInitialized;

export let triggerLinkFollowedEvent: HpTriggerCustomEventFunction<HpTriggerLinkFollowedEventProps> =
  triggerNotYetInitialized;

export function bindCustomChangeEvents({
  onFieldChange,
  onGeneralAction,
  onHelpInfoAction,
  onLinkFollowed,
  onModalAction,
  onNavigationBarAction,
  onNavigationChange,
  onPageLoad,
  onToggleElement
}: HpBindFieldChangeEventsProps): void {
  // The trigger function is the same for all bindings, so it doesn't matter if they are reassigned on subsequent invocations.

  triggerFieldChangedEvent = registerCustomEvent({ eventType: 'hp-field-change', onEventAction: onFieldChange });

  triggerNavigationChangedEvent = registerCustomEvent({
    eventType: 'hp-navigation-change',
    onEventAction: onNavigationChange
  });

  triggerNavigationBarEvent = registerCustomEvent({
    eventType: 'hp-navigation-menu-action',
    onEventAction: onNavigationBarAction
  });

  triggerPageLoadEventPrivate = registerCustomEvent({ eventType: 'hp-page-load', onEventAction: onPageLoad });

  triggerElementToggledEvent = registerCustomEvent({
    eventType: 'hp-toggle-element',
    onEventAction: onToggleElement
  });

  triggerGeneralActionEvent = registerCustomEvent({
    eventType: 'hp-general-action',
    onEventAction: onGeneralAction
  });

  triggerHelpInfoEvent = registerCustomEvent({ eventType: 'hp-help-info-action', onEventAction: onHelpInfoAction });

  triggerModalActionEvent = registerCustomEvent({ eventType: 'hp-modal-action', onEventAction: onModalAction });

  triggerLinkFollowedEvent = registerCustomEvent({ eventType: 'hp-link-followed', onEventAction: onLinkFollowed });
}

export function bindVoidCustomChangeEvents(): void {
  bindCustomChangeEvents({
    onFieldChange: voidFunction,
    onGeneralAction: voidFunction,
    onHelpInfoAction: voidFunction,
    onLinkFollowed: voidFunction,
    onModalAction: voidFunction,
    onNavigationBarAction: voidFunction,
    onNavigationChange: voidFunction,
    onPageLoad: voidFunction,
    onToggleElement: voidFunction
  });
}

function triggerNotYetInitialized(): void {
  throw new HpError('The custom event trigger has not been initialized.');
}

export function registerBespokeCustomEvent<EventPropsT extends HpTriggerCommonEventProps>({
  eventType,
  onEventAction
}: HpRegisterBespokeCustomEventProps<EventPropsT>): HpTriggerBespokeCustomEventFunction<EventPropsT> {
  let hasWindow = false;
  let trigger: HpTriggerBespokeCustomEventFunction<EventPropsT> | undefined;

  useJWindow((win) => {
    hasWindow = true;
    win.on(eventType as any, (event: Event, data: EventPropsT) => {
      onEventAction({ customEvent: event, data, customEventType: eventType });
    });
    trigger = (props) => {
      win.trigger(eventType, props);
    };
  });

  if (!hasWindow) {
    trigger = voidFunction;
  }

  return ensureDefined(trigger, `trigger for '${eventType}'`);
}

export type HpTriggerCustomEventFunction<EventPropsT extends HpTriggerEventProps & HpTriggerCommonEventProps> = (
  props: EventPropsT
) => void;

interface HpRegisterCustomEventProps<EventPropsT extends HpTriggerEventProps & HpTriggerCommonEventProps> {
  eventType: HpCustomEventTypes;
  onEventAction: (props: HpCustomEventHandler<EventPropsT>) => void;
}

function registerCustomEvent<EventPropsT extends HpTriggerEventProps & HpTriggerCommonEventProps>({
  eventType,
  onEventAction
}: HpRegisterCustomEventProps<EventPropsT>): HpTriggerCustomEventFunction<EventPropsT> {
  return registerBespokeCustomEvent({ eventType, onEventAction: onEventAction as any });
}

function useJWindow(action: (win: JQuery<Window & typeof globalThis>) => void): void {
  if (jwin) {
    action(jwin);
  }
}
